// file: src/services/dialogServices.js
import request from './requestServices';
import { listKnowledgeBases } from './kbServices';
import { DIALOG_SET_URL, DIALOG_LIST_URL, DIALOG_RM_URL, DIALOG_GET_URL } from './apiRoutes';

/**
 * Creates a new Dialog that references relevant KBs.
 * - If projectId is provided, it will only use KBs from that project
 * - Uses existing KBs only and doesn't create a default one
 * - If preFetchedKBs is provided, skips calling listKnowledgeBases to improve performance
 * 
 * @param {string} name - Name of the dialog
 * @param {string} projectId - Project ID to associate with this dialog (required for project separation)
 * @param {Array} preFetchedKBs - Optional pre-fetched knowledge bases
 * @returns {Promise<Object>} - Dialog object
 */
export async function createDialog(name, projectId = null, preFetchedKBs = null) {
  // 1) Retrieve all existing KBs or use pre-fetched data
  const allKbs = preFetchedKBs || await listKnowledgeBases();

  let kbIds = [];
  
  // Filter KBs based on project context if projectId is provided
  const relevantKbs = projectId 
    ? allKbs.filter(kb => kb.project_id === projectId)
    : allKbs;

  if (relevantKbs.length > 0) {
    // Use only the relevant KB IDs if they exist
    kbIds = relevantKbs.map(kb => kb.id);
    console.log(`Creating dialog with ${kbIds.length} KB IDs from project ${projectId}`);
  } else {
    console.log(`No relevant KBs found for project ${projectId}, using empty KB IDs array`);
  }
  // Otherwise, kbIds remains an empty array - we don't create a default KB

  // 2) Call /dialog/set with the KB IDs (or empty array)
  const payload = {
    name,
    kb_ids: kbIds,
  };
  
  // Always include project_id in the payload when provided
  if (projectId) {
    payload.project_id = projectId;
    console.log(`Associating dialog with project_id: ${projectId}`);
  } else {
    console.log('Creating dialog without project association');
  }
  
  const { data } = await request(DIALOG_SET_URL, {
    method: 'POST',
    data: payload,
  });
  return data.data;
}

/**
 * Updates an existing dialog's properties (partial update).
 */
export async function updateDialog(dialogId, updates) {
  const payload = {
    dialog_id: dialogId,
    ...updates
  };
  const { data } = await request(DIALOG_SET_URL, {
    method: 'POST',
    data: payload,
  });
  return data.data;
}

/**
 * Lists all valid dialogs owned by the current user.
 * @param {string} [projectId] - Optional project ID to filter dialogs by project
 */
export async function listDialogs(projectId = null) {
  const params = {};
  if (projectId) {
    params.project_id = projectId;
  }
  
  const { data } = await request(DIALOG_LIST_URL, {
    method: 'GET',
    params,
  });
  return data.data; // array
}

/**
 * Removes (soft-deletes) one or more dialogs by ID.
 */
export async function removeDialog(dialogIds) {
  if (!Array.isArray(dialogIds)) {
    dialogIds = [dialogIds];
  }
  const { data } = await request(DIALOG_RM_URL, {
    method: 'POST',
    data: { dialog_ids: dialogIds },
  });
  return data; // Typically returns { "RetCode":0, data:true }
}

/**
 * Gets detailed information about a specific dialog.
 * This includes the KB IDs associated with the dialog.
 * @param {string} dialogId
 * @param {boolean} [suppressNotFound=false] - If true, returns null on 404 instead of throwing.
 */
export async function getDialogDetails(dialogId, suppressNotFound = false) {
  try {
    // Pass suppressNotFoundLog down to the request function
    const { data } = await request(`${DIALOG_GET_URL}?dialog_id=${dialogId}`, {
      method: 'GET',
      suppressNotFoundLog: suppressNotFound // Pass the flag here
    });
    return data.data;
  } catch (error) {
    // Check if it's a "Not Found" error and suppress if requested
    const isNotFound = error?.response?.status === 404 || 
                      (typeof error?.message === 'string' && error.message.toLowerCase().includes('not found'));
                      
    if (isNotFound && suppressNotFound) {
      // console.log already handled by request function if suppressNotFoundLog is true
      // console.log(`[dialogServices] Suppressed not found error for dialog ${dialogId}`);
      return null; // Return null instead of throwing
    }
    // Otherwise, re-throw the error (logging was already done by requestService)
    // console.error(`Error fetching dialog details for ${dialogId}:`, error);
    throw error; 
  }
}

